// Turns the flat knockout Match[] into the two-sided "poster" bracket structure
// the desktop view renders: outer Round-of-32 on both edges converging to the
// Final in the centre.
//
// Pairing/seeding note: the bracket position of each tie is taken from the order
// matches arrive in per round (top→bottom), with the first half feeding the left
// side and the second half the right. The mock is authored in that order; when
// the live endpoint is wired up the backend must return knockout matches in
// bracket order for the same to hold.
import { Match } from "../../types/Match";
import { Team } from "../../types/Team";
import { KnockoutStage } from "../../types/TournamentMatch";

// Defensive TBD check: the live endpoint only returns ties with both teams set,
// but a missing/placeholder team (null, sentinel id -1, or a "TBD" name) is
// still rendered as an undecided slot rather than crashing the card.
export function isTbd(team: Team | undefined | null): boolean {
  return !team || team.id === -1 || team.internationalName === "TBD";
}

export type Winner = "home" | "away" | null;

/** Decides a knockout tie: regular score, then extra time total, then penalties. null while unplayed/drawn. */
export function getWinner(match: Match): Winner {
  if (!match.wasPlayed) return null;
  const h = match.homeScoreRegular;
  const a = match.awayScoreRegular;
  if (h > a) return "home";
  if (a > h) return "away";
  // Regular time tied — check extra-time totals
  const ht = match.homeScoreTotal ?? null;
  const at = match.awayScoreTotal ?? null;
  if (ht !== null && at !== null && ht !== at) return ht > at ? "home" : "away";
  // Still tied — check penalties
  const hp = match.homeScorePenalty ?? null;
  const ap = match.awayScorePenalty ?? null;
  if (hp !== null && ap !== null && hp !== ap) return hp > ap ? "home" : "away";
  return null;
}

/** Tie level after regular time but decided in extra time (no penalties). */
export function wasDecidedInExtraTime(match: Match): boolean {
  if (!match.wasPlayed) return false;
  if (match.homeScoreRegular !== match.awayScoreRegular) return false;
  const ht = match.homeScoreTotal ?? null;
  const at = match.awayScoreTotal ?? null;
  return ht !== null && at !== null && ht !== at;
}

/** Tie level after regular time but decided on penalties → show the "(p)" result. */
export function wasDecidedOnPenalties(match: Match): boolean {
  return (
    match.wasPlayed &&
    match.homeScoreRegular === match.awayScoreRegular &&
    match.homeScorePenalty != null &&
    match.awayScorePenalty != null
  );
}

export function isTbdMatch(match: Match): boolean {
  return isTbd(match.homeTeam) || isTbd(match.awayTeam);
}

export interface BracketColumn {
  stage: KnockoutStage;
  label: string;
  matches: Match[];
}

export interface Bracket {
  /** Outer→inner: R32, R16, QF, SF. */
  left: BracketColumn[];
  /** Inner→outer (SF nearest centre): SF, QF, R16, R32. */
  right: BracketColumn[];
  final?: Match;
  thirdPlace?: Match;
}

const STAGE_LABEL: Record<KnockoutStage, string> = {
  "Round of 32": "Round of 32",
  "Round of 16": "Round of 16",
  "Quarter-finals": "Quarter-finals",
  "Semi-finals": "Semi-finals",
  "Play-off for third place": "Third place",
  Final: "Final",
};

// Canonical backend Stage enum value (RoundNumber) per knockout round. This is
// the reliable key for filtering live matches — the DTO's `value` string comes
// straight from FIFA's stage description and isn't guaranteed to match these
// labels, whereas RoundNumber is set deterministically server-side.
export const STAGE_ROUND_NUMBER: Record<KnockoutStage, number> = {
  "Round of 32": 5,
  "Round of 16": 6,
  "Quarter-finals": 7,
  "Semi-finals": 8,
  "Play-off for third place": 9,
  Final: 10,
};

/** Matches belonging to a knockout round, keyed by the reliable RoundNumber. */
export function matchesInStage(matches: Match[], stage: KnockoutStage): Match[] {
  return matches.filter(
    (m) => m.matchType.roundNumber === STAGE_ROUND_NUMBER[stage]
  );
}

// Outer→inner order of the rounds that split across the two sides.
const SIDE_STAGES: KnockoutStage[] = [
  "Round of 32",
  "Round of 16",
  "Quarter-finals",
  "Semi-finals",
];

export function buildBracket(matches: Match[]): Bracket {
  const byStage = (stage: KnockoutStage) => matchesInStage(matches, stage);

  const left: BracketColumn[] = [];
  const right: BracketColumn[] = [];

  for (const stage of SIDE_STAGES) {
    const all = byStage(stage);
    const half = Math.ceil(all.length / 2);
    left.push({ stage, label: STAGE_LABEL[stage], matches: all.slice(0, half) });
    right.push({ stage, label: STAGE_LABEL[stage], matches: all.slice(half) });
  }

  return {
    left,
    // Render right side inner→outer so Semi-finals sit beside the centre.
    right: [...right].reverse(),
    final: byStage("Final")[0],
    thirdPlace: byStage("Play-off for third place")[0],
  };
}

export function bracketHasMatches(matches: Match[]): boolean {
  return matches.some((m) => m.matchType.type === "Knockout");
}
